import React, { useEffect, useRef, useMemo, Fragment, useContext } from "react";
import { useSelector } from "react-redux";
import Fuse from "fuse.js";
import { debounce } from "@utils/lodash";
import Context from "@components/useContext/context";

import { Model, Variant } from "@store/mmvData/types";

import InputField from "@components/common/mobile/inputField";

import { StyledChevronDown, StyledPopoverCard } from "../styles";

import { ERROR_ACCENT, FUSE_OPTIONS } from "@utils/bike/bikeConstants";

import removeModalBackgroundScroll from "utils/removeModalBackgroundScroll";

import {
  PopularHeading,
  ListItem,
  Wrapper,
  ErrLabel,
  VehicleContainer
} from "./styles";

import { ModalMobile as MobileModal } from "@acko-tech/building-blocks.ui.modal";
import FIELD_ERROR_MESSAGE from "@utils/errorMessages";
import { styled } from "@acko-tech/building-blocks.ui.common";
import Input from "@acko-tech/building-blocks.ui.search-input";
// import FormData from "@components/common/standAlone/feedBack/formDetails";
// import FeedbackContainer from "@components/common/standAlone/feedBack";

const StyledInput = styled(Input)`
  input {
    font-size: 16px;
  }

  ${props => (props.isDesktop ? `margin-top: 4px` : "")}
  ${props =>
    props.isDesktop
      ? `input {
    font-size: 12px;
  }`
      : ""}
`;

function getSearchResults(
  searchText: string,
  fuseObject: any,
  popularVehicles
): Model[] {
  if (searchText.length < 3) {
    return popularVehicles;
  }

  return fuseObject.search(searchText).slice(0, 15);
}

interface Store {
  vehicles: Model[];
  popularVehicles: Model[];
}

interface Props {
  showMMVPicker: boolean;
  searchIconClick?: any;
  onClick: () => void;
  onChange: (variant: Variant, popular: boolean) => void;
  value?: any;
  setShowMMVPicker: Function;
  bikeNoErr?: any;
  setBikeNoErr?: any;
  searchText?: any;
  setSearchText?: any;
}

const MMVInput: React.FC<Props> = ({
  showMMVPicker,
  onClick,
  onChange,
  // searchIconClick,
  setShowMMVPicker,
  value,
  bikeNoErr,
  setBikeNoErr,
  searchText,
  setSearchText
}) => {
  const { vehicles, popularVehicles }: Store = useSelector(
    ({ mmvDataReducer }: any) => {
      return {
        vehicles: mmvDataReducer?.vehicles ?? [],
        popularVehicles: mmvDataReducer?.popularVehicles ?? []
      };
    }
  );
  const { isDesktop } = useContext(Context);

  useEffect(() => {
    if (searchText.length) {
      setBikeNoErr(false);
    }
  }, [searchText]);

  const fuseRef = useRef(new Fuse(vehicles, FUSE_OPTIONS));

  useEffect(() => {
    fuseRef.current = new Fuse(vehicles, FUSE_OPTIONS);
  }, [vehicles]);

  removeModalBackgroundScroll({ showModal: showMMVPicker });

  // blur out input when scrolling content
  useEffect(() => {
    const element = document.getElementById("scroll-box");
    const removeFocusFromInput = () => {
      // @ts-ignore
      document?.activeElement?.blur();
    };
    // adding event listener for removing focus from input when scrolling
    element?.addEventListener("scroll", removeFocusFromInput);

    return () => {
      element?.removeEventListener("scroll", removeFocusFromInput);
    };
  }, [showMMVPicker]);

  const options = useMemo<Model[]>(
    () => getSearchResults(searchText, fuseRef.current, popularVehicles),
    [searchText, fuseRef.current, popularVehicles]
  );

  const popular = searchText.length < 3;

  return (
    <Wrapper>
      <div style={{ position: "relative" }}>
        <InputField
          placeholder={value ? "Brand & model" : "Select brand & model"}
          value={value}
          errors={{}}
          inputStyles={{
            border: bikeNoErr && `1px solid ${ERROR_ACCENT}`,
            caretColor: "transparent",
            height: "48px",
            color: "#504a6b"
          }}
          inputContainerStyles={{
            marginTop: "2rem"
          }}
          handleClick={() => {
            setSearchText("");
            onClick();
          }}
          onChange={debounce(setSearchText, 50)}
          readOnly
        />
        <StyledChevronDown
          style={{ cursor: "pointer" }}
          onClick={() => {
            setSearchText("");
            onClick();
          }}
        />
      </div>
      {bikeNoErr && <ErrLabel>{FIELD_ERROR_MESSAGE.mmvBike.required}</ErrLabel>}

      {isDesktop ? (
        <>
          {" "}
          <StyledPopoverCard
            show={showMMVPicker}
            onOutsideClick={() => {
              setShowMMVPicker(false);
            }}
          >
            <>
              <InputField
                style={{ width: "100px" }}
                placeholder={"Search your bike"}
                value={searchText}
                errors={{}}
                // handleClick={onClick}
                onChange={debounce(setSearchText, 50)}
                inputContainerStyles={{ margin: "0px" }}
                autoFocus
                showCrossIcon={true}
                name={"bikeMMV"}
              />
              {popular && <PopularHeading>Popular Bikes</PopularHeading>}
              {options.map(option => (
                <Fragment key={option.id}>
                  {option.variants?.map(variant => {
                    const vehicleMake = `${option.name} ${
                      variant.fuelType === "electric"
                        ? `${(variant.cc / 1000).toFixed(1)} kW`
                        : `${variant.cc}CC`
                    }`;
                    variant.verbose = vehicleMake;

                    return (
                      <ListItem
                        key={variant.id}
                        onClick={() => {
                          onChange(variant, popular);
                          setSearchText(variant.verbose);
                        }}
                      >
                        {variant.verbose}
                      </ListItem>
                    );
                  })}
                </Fragment>
              ))}
              {/* {!popular && !isDesktop && (
                <div
                  style={{
                    padding: "20px 0px 0px",
                    width: "100%",
                    position: "relative"
                  }}
                >
                  <FeedbackContainer
                    isDesktop={isDesktop}
                    TouchPoint="mmv"
                    formInputData={FormData.bikeMakePageData}
                    Icon={"bike"}
                  />
                </div>
              )} */}
            </>
          </StyledPopoverCard>
        </>
      ) : (
        <MobileModal
          urlhash=""
          show={showMMVPicker}
          showOverlayBackground
          onHide={() => {
            setShowMMVPicker(false);
          }}
          header={
            <div
              style={{
                fontSize: "18px",
                fontWeight: 600,
                lineHeight: "22px",
                color: "#040222"
              }}
            >
              Find your bike
            </div>
          }
          padding={"24px 20px 20px"}
          wrapperStyles={{
            maxHeight: "95%",
            minHeight: "90%",
            borderRadius: "24px 24px 0 0"
          }}
        >
          <>
            <div
              style={{
                paddingBottom: "1rem"
              }}
            >
              <StyledInput
                handleChange={debounce(setSearchText, 50)}
                onFocus={() => {
                  onClick && onClick();
                }}
                placeholder={"Search your Bike"}
                backgroundColor={isDesktop ? "#F8F8FB" : ""}
                showCrossIcon={true}
              />
            </div>
            {popular && <PopularHeading>Top Search Results</PopularHeading>}
            <VehicleContainer id="scroll-box">
              <>
                {options.map(option => (
                  <Fragment key={option.id}>
                    {option.variants?.map(variant => {
                      const vehicleMake = `${option.name} ${
                        variant.fuelType === "electric"
                          ? `${(variant.cc / 1000).toFixed(1)} kW`
                          : `${variant.cc}CC`
                      }`;
                      variant.verbose = vehicleMake;

                      return (
                        <ListItem
                          key={variant.id}
                          onClick={() => {
                            onChange(variant, popular);
                            setSearchText(variant.verbose);
                          }}
                        >
                          {variant.verbose}
                        </ListItem>
                      );
                    })}
                  </Fragment>
                ))}
                {/* {!popular && !isDesktop && (
                  <div style={{ padding: "16px 0 12px 0" }}>
                    <FeedbackContainer
                      isDesktop={isDesktop}
                      TouchPoint="mmv"
                      formInputData={FormData.bikeMakePageData}
                      Icon={"bike"}
                    />
                  </div>
                )} */}
              </>
            </VehicleContainer>
          </>
        </MobileModal>
      )}
    </Wrapper>
  );
};

export default MMVInput;
